import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import useAxios from "../../../../../utility/axios-token-manager/init";
import Flash from "../../../../../utility/Flash";
import { getUserProfile } from "../../../../../redux/actions/profile";
import { loadStart, loadStop } from "../../../../../redux/actions/loading";
import { ImageResizer } from "../../../../ImageResizer";
import BankList from "../../../../bankList";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

const TABS = [
  { id: "profile", label: "Profile", icon: "bi bi-person-fill" },
  { id: "bank", label: "Bank Account", icon: "bi bi-bank2" },
  { id: "security", label: "Security", icon: "bi bi-shield-lock-fill" },
];

function AvatarUploader({ src, onFile, label }) {
  return (
    <div style={styles.avatarWrap}>
      <div style={styles.avatarFrame}>
        {src ? (
          <img src={src} alt={label} style={styles.avatarImg} />
        ) : (
          <i className="bi bi-person-circle" style={{ fontSize: 56, color: "#ccc" }} />
        )}
        <label style={styles.avatarEdit}>
          <i className="bi bi-camera-fill" />
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} />
        </label>
      </div>
      <div style={styles.avatarLabel}>{label}</div>
    </div>
  );
}

function Settings({ profile }) {
  const dispatch = useDispatch();
  const [tab, setTab] = useState("profile");

  // Profile form
  const [bio, setBio] = useState({ fullName: "", mobile: "", address: "" });
  useEffect(() => {
    if (profile) setBio({ fullName: profile.fullName || "", mobile: profile.mobile || "", address: profile.address || "" });
  }, [profile]);

  const handleBioChange = (e) => setBio({ ...bio, [e.target.name]: e.target.value });

  const saveBio = async () => {
    try {
      dispatch(loadStart());
      const res = await useAxios.put("/sales-agent/my-profile", { ...profile, ...bio });
      dispatch(loadStop());
      if (res.data.code === 201) {
        Flash("success", "Profile updated", "", 3000);
        dispatch(getUserProfile());
      } else {
        Flash("error", res.data.message || "Update failed", "", 3000);
      }
    } catch {
      dispatch(loadStop());
      Flash("error", "Server error", "", 3000);
    }
  };

  // Image upload
  const uploadToS3 = async (file, field) => {
    try {
      let img = file.size > 300000 ? await ImageResizer(file) : file;
      img = await ImageResizer(img);
      const form = new FormData();
      form.append("image", img);
      dispatch(loadStart());
      const res = await useAxios.post("/upload/affiliates", form, { headers: { "Content-Type": "multipart/form-data" } });
      dispatch(loadStop());
      if (res.data.code === 201) {
        await useAxios.put("/sales-agent/my-upload", { field, data: res.data.data });
        dispatch(getUserProfile());
        Flash("success", "Image updated", "", 3000);
      }
    } catch {
      dispatch(loadStop());
      Flash("error", "Upload failed", "", 3000);
    }
  };

  const handleFile = (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return Flash("error", "Image files only", "", 3000);
    await uploadToS3(file, field);
  };

  // Bank form
  const [bank, setBank] = useState({ bank: "", accountNo: "", accountName: "" });
  useEffect(() => {
    if (profile?.accountDetails) setBank({ ...profile.accountDetails });
  }, [profile]);

  const handleBankChange = (e) => setBank({ ...bank, [e.target.name]: e.target.value });

  const saveBank = async () => {
    try {
      dispatch(loadStart());
      const res = await useAxios.put("/sales-agent/my-settings/bank", bank);
      dispatch(loadStop());
      if (res.data.code === 201) {
        Flash("success", "Bank info updated", "", 3000);
        dispatch(getUserProfile());
      } else {
        Flash("error", "Update failed", "", 3000);
      }
    } catch {
      dispatch(loadStop());
      Flash("error", "Server error", "", 3000);
    }
  };

  // Password form
  const [pwd, setPwd] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [showPwd, setShowPwd] = useState({ current: false, new: false, confirm: false });

  const savePassword = async () => {
    if (!pwd.currentPassword || !pwd.newPassword || !pwd.confirm)
      return Flash("error", "All password fields are required", "", 3000);
    if (pwd.newPassword !== pwd.confirm)
      return Flash("error", "New passwords do not match", "", 3000);
    if (pwd.newPassword.length < 8)
      return Flash("error", "Password must be at least 8 characters", "", 3000);
    try {
      dispatch(loadStart());
      const res = await useAxios.put("/sales-agent/change-password", {
        currentPassword: pwd.currentPassword,
        newPassword: pwd.newPassword,
      });
      dispatch(loadStop());
      if (res.data.success) {
        Flash("success", "Password changed successfully", "", 3000);
        setPwd({ currentPassword: "", newPassword: "", confirm: "" });
      } else {
        Flash("error", res.data.message || "Failed to change password", "", 3000);
      }
    } catch {
      dispatch(loadStop());
      Flash("error", "Server error", "", 3000);
    }
  };

  return (
    <div style={styles.root}>
      <h2 style={styles.heading}>Settings</h2>
      <p style={styles.subText}>Manage your profile, bank account and security.</p>

      {/* Account Info Banner */}
      <div style={styles.banner}>
        <div style={styles.bannerAvatar}>
          {profile?.passport?.Location
            ? <img src={profile.passport.Location} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <i className="bi bi-person-fill" style={{ fontSize: 28, color: "#fff" }} />}
        </div>
        <div>
          <div style={styles.bannerName}>{profile?.fullName || "Agent"}</div>
          <div style={styles.bannerEmail}>{profile?.email}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
            {profile?.agentCode && (
              <span style={styles.pill}>Code: <strong style={{ marginLeft: 4 }}>{profile.agentCode}</strong></span>
            )}
            <span style={{ ...styles.pill, background: profile?.approve ? "rgba(167,204,72,0.2)" : "rgba(230,57,70,0.15)", color: profile?.approve ? "#5a8a00" : "#e63946" }}>
              {profile?.approve ? "Approved" : "Pending"}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {TABS.map((t) => (
          <button key={t.id} style={{ ...styles.tab, ...(tab === t.id ? styles.tabActive : {}) }} onClick={() => setTab(t.id)}>
            <i className={t.icon} style={{ marginRight: 6 }} />{t.label}
          </button>
        ))}
      </div>

      {/* Profile Tab */}
      {tab === "profile" && (
        <div style={styles.card}>
          {/* Avatars */}
          <div style={styles.avatarRow}>
            <AvatarUploader
              src={profile?.passport?.Location}
              label="Profile Photo"
              onFile={handleFile("passport")}
            />
            <AvatarUploader
              src={profile?.id?.Location}
              label="Identity Card"
              onFile={handleFile("id")}
            />
          </div>
          <hr style={styles.divider} />
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Full Name</label>
              <input style={styles.input} name="fullName" value={bio.fullName} onChange={handleBioChange} placeholder="Full name" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone Number</label>
              <input style={styles.input} name="mobile" value={bio.mobile} onChange={handleBioChange} placeholder="Phone number" type="tel" />
            </div>
            <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
              <label style={styles.label}>Address</label>
              <input style={styles.input} name="address" value={bio.address} onChange={handleBioChange} placeholder="Residential address" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input style={{ ...styles.input, background: "#f8f8f8", color: "#aaa" }} value={profile?.email || ""} readOnly />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Sector</label>
              <input style={{ ...styles.input, background: "#f8f8f8", color: "#aaa" }} value={profile?.sector ? "Sector " + profile.sector : "Not assigned"} readOnly />
            </div>
          </div>
          <button style={styles.saveBtn} onClick={saveBio}>
            <i className="bi bi-check2" style={{ marginRight: 6 }} />Save Profile
          </button>
        </div>
      )}

      {/* Bank Tab */}
      {tab === "bank" && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>
            <i className="bi bi-bank2" style={{ color: PRIMARY, marginRight: 8 }} />Bank Account Details
          </div>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Bank Name</label>
              <select style={{ ...styles.input, background: "#fff" }} name="bank" value={bank.bank} onChange={handleBankChange}>
                <option value="">Select Bank</option>
                {BankList.map((b, i) => (
                  <option key={i} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Account Number</label>
              <input
                style={styles.input} name="accountNo" value={bank.accountNo}
                onChange={(e) => { if (e.target.value.length <= 11) handleBankChange(e); }}
                placeholder="10-digit account number" type="number"
              />
            </div>
            <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
              <label style={styles.label}>Account Name</label>
              <input style={styles.input} name="accountName" value={bank.accountName} onChange={handleBankChange} placeholder="Account holder name" />
            </div>
          </div>
          <button style={styles.saveBtn} onClick={saveBank}>
            <i className="bi bi-check2" style={{ marginRight: 6 }} />Save Bank Info
          </button>
        </div>
      )}

      {/* Security Tab */}
      {tab === "security" && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>
            <i className="bi bi-shield-lock-fill" style={{ color: PRIMARY, marginRight: 8 }} />Change Password
          </div>
          <div style={{ maxWidth: 440 }}>
            {[
              { key: "currentPassword", label: "Current Password", field: "current" },
              { key: "newPassword", label: "New Password", field: "new" },
              { key: "confirm", label: "Confirm New Password", field: "confirm" },
            ].map(({ key, label, field }) => (
              <div style={styles.formGroup} key={key}>
                <label style={styles.label}>{label}</label>
                <div style={styles.pwdWrap}>
                  <input
                    style={{ ...styles.input, paddingRight: 44 }}
                    type={showPwd[field] ? "text" : "password"}
                    value={pwd[key]}
                    onChange={(e) => setPwd({ ...pwd, [key]: e.target.value })}
                    placeholder={label}
                  />
                  <button
                    style={styles.eyeBtn}
                    type="button"
                    onClick={() => setShowPwd({ ...showPwd, [field]: !showPwd[field] })}
                  >
                    <i className={showPwd[field] ? "bi bi-eye-slash" : "bi bi-eye"} />
                  </button>
                </div>
              </div>
            ))}
            <div style={styles.pwdHint}>Minimum 8 characters</div>
            <button style={styles.saveBtn} onClick={savePassword}>
              <i className="bi bi-lock-fill" style={{ marginRight: 6 }} />Change Password
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  root: { padding: "0 0 40px 0" },
  heading: { fontSize: 22, fontWeight: 700, color: BG, marginBottom: 4 },
  subText: { color: "#888", fontSize: 13, marginBottom: 24 },
  banner: {
    background: BG, borderRadius: 12, padding: "20px 24px",
    display: "flex", alignItems: "center", gap: 20, marginBottom: 24, flexWrap: "wrap",
  },
  bannerAvatar: {
    width: 60, height: 60, borderRadius: "50%", background: "rgba(255,255,255,0.15)",
    display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0,
  },
  bannerName: { fontSize: 18, fontWeight: 700, color: "#fff" },
  bannerEmail: { fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 2 },
  pill: { background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.7)", borderRadius: 20, padding: "2px 12px", fontSize: 11, display: "inline-flex", alignItems: "center" },
  tabs: { display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" },
  tab: { padding: "8px 18px", borderRadius: 8, border: "1.5px solid #e0e0e0", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500, display: "inline-flex", alignItems: "center", color: "#555" },
  tabActive: { background: PRIMARY, color: "#fff", borderColor: PRIMARY },
  card: { background: "#fff", borderRadius: 12, padding: "24px 28px", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: BG, marginBottom: 20, display: "flex", alignItems: "center" },
  avatarRow: { display: "flex", gap: 32, flexWrap: "wrap", marginBottom: 24 },
  avatarWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 8 },
  avatarFrame: {
    width: 90, height: 90, borderRadius: "50%", border: "3px solid " + PRIMARY,
    position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
    overflow: "hidden", background: "#f5f5f5",
  },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  avatarEdit: {
    position: "absolute", bottom: 0, right: 0, width: 26, height: 26, borderRadius: "50%",
    background: PRIMARY, display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", color: "#fff", fontSize: 12,
  },
  avatarLabel: { fontSize: 12, color: "#888", fontWeight: 500 },
  divider: { border: "none", borderTop: "1px solid #f0f0f0", margin: "0 0 20px 0" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16, marginBottom: 24 },
  formGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: 0.5 },
  input: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none", width: "100%", boxSizing: "border-box" },
  saveBtn: {
    background: PRIMARY, color: "#fff", border: "none", borderRadius: 8,
    padding: "11px 24px", cursor: "pointer", fontSize: 14, fontWeight: 600,
    display: "inline-flex", alignItems: "center",
  },
  pwdWrap: { position: "relative" },
  eyeBtn: {
    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
    background: "none", border: "none", cursor: "pointer", color: "#aaa", fontSize: 16,
  },
  pwdHint: { fontSize: 11, color: "#aaa", marginBottom: 16 },
};

const mapStateToProps = (state) => ({
  profile: state.profile.userProfile,
});
export default connect(mapStateToProps)(Settings);
